import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Wraps the slot the plan broadcasts into (plans/_changed). Each notice
// arrives hidden carrying the plan's version and who changed it. A newer
// version from someone else means the page is stale; the viewer's own saves
// are ignored, since the paths that update the page in place never reload it
// and would otherwise announce the viewer's edit back to them.
//
// With the live_plan_refresh flag on (liveValue), a stale page refreshes
// itself with a Turbo page refresh (morph, scroll preserved; after_morph.js
// puts the UI state back), so other people's edits show up live. The one
// exception is a viewer mid-edit outside a modal: a morph would reset what
// they are typing, so they get the notice and a button instead. Open modals
// are data-turbo-permanent and ride a morph out (see
// setup/modal_morph_persistence.js), so edits in them don't hold it up. With
// the flag off every stale page gets the notice; its button does the same
// in-place refresh.
//
// `latest` is the newest foreign version seen. The morph brings the server's
// version in through versionValue, so comparing the two after it tells
// whether a change landed while the refresh was in flight (Turbo drops a
// refresh during a visit) and needs one more.

// Keeps html.live-refreshing on until every live refresh in flight has
// finished with the progress bar. After load Turbo still runs the bar to 100%
// and fades it before removing it, so each refresh lets go when the bar
// leaves the DOM (or at load, when the refresh was quick enough never to
// show one). Counted, because the next refresh can start while the last
// bar is still fading. A refresh Turbo drops during another visit settles
// on that visit's load.
let liveRefreshes = 0

function quietUntilSettled() {
  const root = document.documentElement
  liveRefreshes++
  root.classList.add("live-refreshing")

  const settle = () => {
    if (--liveRefreshes === 0) root.classList.remove("live-refreshing")
  }

  document.addEventListener("turbo:load", () => {
    const bar = root.querySelector(":scope > .turbo-progress-bar")
    if (!bar) return settle()

    new MutationObserver((_, observer) => {
      if (bar.isConnected) return
      observer.disconnect()
      settle()
    }).observe(root, { childList: true })
  }, { once: true })
}

export default class extends Controller {
  static targets = ["banner"]
  static values = { version: Number, user: Number, live: Boolean }

  versionValueChanged() {
    this.sync()
  }

  bannerTargetConnected(banner) {
    const version = Number(banner.dataset.version)
    const foreign = Number(banner.dataset.actor) !== this.userValue
    if (foreign && version > (this.latest || 0)) this.latest = version
    this.sync()
  }

  sync() {
    const stale = this.latest > this.versionValue
    if (!this.hasBannerTarget) return

    if (stale && this.liveValue && !this.editing) {
      // Starting a visit stops any form submission in flight (Turbo's
      // navigator.stop), so refreshing now would silently drop the viewer's
      // own click. submitted() syncs again once it has landed.
      if (!this.submitting) this.liveRefresh()
      return
    }

    this.bannerTarget.hidden = !stale
  }

  // turbo:submit-end fires before Turbo's navigator lets go of the
  // submission, so wait a tick before a refresh could start a visit.
  submitted() {
    setTimeout(() => this.sync())
  }

  refresh() {
    this.bannerTarget.setAttribute("aria-busy", "true")
    Turbo.session.refresh(window.location.href)
  }

  // A refresh someone else's edit set off isn't a wait the viewer asked for,
  // so it runs without Turbo's progress bar (hidden in _plan_changed.scss).
  // The notice's button still shows it: that refresh the viewer clicked for.
  liveRefresh() {
    quietUntilSettled()
    this.refresh()
  }

  // A page-level form submission (button_to, turbo_method links) still in
  // flight, from the click (or its turbo-confirm dialog) to submit-end. Frame
  // submissions, modals' included, live on their frame and survive a refresh.
  // Reads Turbo's navigator, not public API (checked on Turbo 8.0.x), because
  // the public signals (aria-busy, turbo:submit-start) only arrive once the
  // fetch is prepared, too late to cover a refresh landing right on the click.
  get submitting() {
    const submission = Turbo.session.navigator.formSubmission
    return !!submission && submission.state !== "stopped"
  }

  // A dirty form or a field being typed in, outside any open modal.
  get editing() {
    const focused = document.activeElement
    const typing = focused?.matches("input:not([type=checkbox]):not([type=radio]), textarea, [contenteditable]")

    return [document.querySelector("form[data-dirty]"), typing && focused]
      .some(element => element && !element.closest(".modal.show"))
  }
}
